var m = require('mithril');
var prop = require('mithril/stream');
var loaders = require('../layout/components/loaders');
var modal = require('../layout/components/modal');
var buttonElement = require('../components/button');
var flash = require('../components/flash');
var ToggleSwitch = require('../components/toggleSwitch');

var state = {
  enabled: prop(false),
  tenantId: prop(''),
  clientId: prop(''),
  driveId: prop(''),
  defaultFolder: prop(''),
  authMethod: prop('certificate'),
  clientSecret: prop(''),
  pfxPassword: prop(''),
  certificateFile: null,
  settings: null,
  loading: true,
  saving: false,
  testing: false,
  activatingCertificate: false,
  message: prop([]),
  error: prop([]),
  certificateError: prop([])
};

function resetAlerts() {
  state.message([]);
  state.error([]);
}

function errorMessage(error, fallback) {
  if (error && error.error) return error.error;
  if (error && error.message) return error.message;
  return fallback;
}

function applySettings(settings) {
  state.settings = settings || {};
  state.enabled(settings && typeof settings.enabled === 'boolean'
    ? settings.enabled
    : Boolean(settings && settings.configured));
  state.tenantId(settings && settings.tenantId || '');
  state.clientId(settings && settings.clientId || '');
  state.driveId(settings && settings.driveId || '');
  state.defaultFolder(settings && settings.defaultFolder || '');
  state.authMethod(settings && settings.authMethod || 'certificate');
  state.clientSecret('');
}

function loadSettings(options) {
  options = options || {};
  if (!options.keepAlerts) resetAlerts();
  state.loading = true;
  return loaders.requestWithLoader({
    loader: 'background',
    method: 'GET',
    url: '/api/v1/report/delivery/onedrive/settings'
  }).then(function (settings) {
    applySettings(settings);
  }).catch(function (error) {
    state.error([errorMessage(error, 'Could not load SharePoint delivery settings.')]);
  }).finally(function () {
    state.loading = false;
    m.redraw();
  });
}

function commonPayload() {
  return {
    enabled: Boolean(state.enabled()),
    tenantId: String(state.tenantId() || '').trim(),
    clientId: String(state.clientId() || '').trim(),
    driveId: String(state.driveId() || '').trim(),
    defaultFolder: String(state.defaultFolder() || '').trim(),
    authMethod: state.authMethod()
  };
}

function validateCommon() {
  var errors = [];
  if (!String(state.tenantId() || '').trim()) errors.push('Tenant ID is required.');
  if (!String(state.clientId() || '').trim()) errors.push('Client ID is required.');
  if (!String(state.driveId() || '').trim()) errors.push('Document library ID is required.');
  return errors;
}

function saveSettings() {
  resetAlerts();
  var errors = validateCommon();
  if (errors.length) {
    state.error(errors);
    return;
  }
  var payload = commonPayload();
  var secret = String(state.clientSecret() || '');
  if (secret) payload.clientSecret = secret;
  state.saving = true;
  return loaders.requestWithLoader({
    loader: 'submit',
    method: 'PUT',
    url: '/api/v1/report/delivery/onedrive/settings',
    body: payload
  }).then(function (settings) {
    applySettings(settings);
    state.message(['Saved SharePoint delivery settings.']);
  }).catch(function (error) {
    state.error([errorMessage(error, 'Could not save SharePoint delivery settings.')]);
  }).finally(function () {
    state.saving = false;
    m.redraw();
  });
}

function testConnection() {
  resetAlerts();
  state.testing = true;
  return loaders.requestWithLoader({
    loader: 'background',
    method: 'POST',
    url: '/api/v1/report/delivery/onedrive/settings/test',
    body: {}
  }).then(function (result) {
    if (result && result.settings) applySettings(result.settings);
    state.message(['Connection test succeeded.']);
  }).catch(function (error) {
    state.error([errorMessage(error, 'Could not connect to SharePoint.')]);
  }).finally(function () {
    state.testing = false;
    m.redraw();
  });
}

function parseFetchError(response) {
  return response.text().then(function (text) {
    var message = text;
    try {
      var parsed = JSON.parse(text);
      message = parsed.error || parsed.message || text;
    } catch (error) {
      // Use the plain response body.
    }
    var requestError = new Error(message || 'Certificate activation failed.');
    requestError.status = response.status;
    throw requestError;
  });
}

function activateCertificate() {
  state.certificateError([]);
  var errors = validateCommon();
  if (!state.certificateFile) errors.push('Choose a PFX or P12 certificate file.');
  if (errors.length) {
    state.certificateError(errors);
    return;
  }

  var formData = new FormData();
  var payload = commonPayload();
  Object.keys(payload).forEach(function (name) {
    formData.append(name, payload[name]);
  });
  formData.append('password', String(state.pfxPassword() || ''));
  formData.append('certificate', state.certificateFile);

  state.activatingCertificate = true;
  return fetch('/api/v1/report/delivery/onedrive/settings/certificate', {
    method: 'POST',
    credentials: 'same-origin',
    body: formData
  }).then(function (response) {
    if (!response.ok) return parseFetchError(response);
    return response.json();
  }).then(function (result) {
    if (result && result.settings) applySettings(result.settings);
    state.pfxPassword('');
    state.certificateFile = null;
    modal.vm.close();
    state.message(['The certificate was tested and activated.']);
  }).catch(function (error) {
    state.certificateError([errorMessage(error, 'Could not import the SharePoint certificate.')]);
  }).finally(function () {
    state.activatingCertificate = false;
    m.redraw();
  });
}

function certificateUploadModalView() {
  return m('div', [
    state.certificateError().length ? m(flash, { items: state.certificateError(), bg: 'bg-red' }) : null,
    m('p.mt0.gray', 'Upload the public certificate to the Entra application first. Nimrod will test this PFX certificate and the configured SharePoint destination before activating it.'),
    m('div.mb3', [
      m('label.db.mb1', { for: 'sharepoint-certificate-file' }, 'PFX or P12 Certificate'),
      m('input.input-reset.w-100.pa2.ba.b--black-20', {
        id: 'sharepoint-certificate-file',
        type: 'file',
        accept: '.pfx,.p12,application/x-pkcs12',
        onchange: function (event) {
          state.certificateFile = event.target.files && event.target.files[0] || null;
        }
      })
    ]),
    m('div.mb2', [
      m('label.db.mb1', { for: 'sharepoint-certificate-password' }, 'PFX Password'),
      m('input.form-control.w-100', {
        id: 'sharepoint-certificate-password',
        type: 'password',
        autocomplete: 'new-password',
        value: state.pfxPassword(),
        oninput: function (event) { state.pfxPassword(event.target.value); }
      })
    ]),
    m('div.f6.gray', 'The password is used only to import the certificate and is not stored.')
  ]);
}

function openCertificateModal() {
  var replacing = Boolean(state.settings && state.settings.hasCertificate);
  resetAlerts();
  state.certificateError([]);
  state.pfxPassword('');
  state.certificateFile = null;
  modal.vm.title(replacing ? 'Replace Certificate' : 'Upload Certificate');
  modal.vm.body = certificateUploadModalView;
  modal.vm.buttons([{
    label: replacing ? 'Test & Replace' : 'Test & Activate',
    class: 'btn-good',
    disabled: function () {
      return state.activatingCertificate || !(state.settings && state.settings.credentialEncryptionAvailable);
    },
    func: activateCertificate
  }]);
  modal.vm.open();
}

function dateLabel(value) {
  if (!value) return 'Unknown';
  var date = new Date(Number(value));
  return Number.isNaN(date.valueOf()) ? 'Unknown' : date.toLocaleString();
}

function connectionStatus() {
  var settings = state.settings || {};
  if (!state.enabled()) {
    return m('div.pa2.br2.ba.b--gray.gray', [
      m('strong', 'Disabled'),
      ' · Settings and credentials are retained'
    ]);
  }
  if (settings.error) {
    return m('div.pa2.br2.bg-washed-red.dark-red', settings.error);
  }
  if (!settings.configured) {
    return m('div.pa2.br2.bg-washed-yellow.dark-red', 'Enabled · Setup incomplete' +
      (settings.missing && settings.missing.length ? ': ' + settings.missing.join(', ') : ''));
  }
  return m('div.pa2.br2.bg-washed-green.dark-green', [
    m('strong', 'Enabled · Ready'),
    ' · ',
    settings.authMethod === 'certificate' ? 'Certificate authentication' : 'Client secret authentication',
    settings.source ? ' · ' + (settings.source === 'database' ? 'Saved in Nimrod' : 'Environment fallback') : ''
  ]);
}

function certificateSummary() {
  var certificate = state.settings && state.settings.certificate;
  var settings = state.settings || {};
  var certificateButton = m('div.flex.justify-end.mt3.mt0-ns.flex-none', m(buttonElement, {
    label: settings.hasCertificate ? 'Replace Certificate' : 'Upload Certificate',
    class: 'b--gray',
    disabled: state.activatingCertificate || !settings.credentialEncryptionAvailable,
    func: openCertificateModal
  }));
  if (!certificate) {
    return m('div.flex-ns.items-center-ns.justify-between-ns.ba.b--mid-gray.br2.pa3.mb3', [
      m('div.pr3-ns', [
        m('div.mb1', m('strong', 'No certificate uploaded')),
        m('div.f6.gray', 'Upload a PFX or P12 certificate to enable certificate authentication.')
      ]),
      certificateButton
    ]);
  }
  var stateClass = certificate.expirationState === 'expired' || certificate.expirationState === 'critical'
    ? 'red'
    : (certificate.expirationState === 'warning' || certificate.expirationState === 'notice' ? 'dark-orange' : 'dark-green');
  return m('div.flex-ns.items-center-ns.justify-between-ns.ba.b--mid-gray.br2.pa3.mb3', [
    m('div.pr3-ns', [
      m('div.mb1', [m('strong', certificate.subject || 'Current certificate')]),
      m('div.f6.gray.mb1', 'SHA-256 thumbprint: ' + (certificate.thumbprintSha256 || certificate.thumbprint || 'Unknown')),
      m('div.f6.' + stateClass, [
        'Expires: ' + dateLabel(certificate.expiresAt),
        certificate.daysUntilExpiration != null ? ' (' + certificate.daysUntilExpiration + ' days)' : ''
      ])
    ]),
    certificateButton
  ]);
}

function field(label, value, attributes, help) {
  var inputId = attributes && attributes.id
    ? attributes.id
    : 'sharepoint-' + String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return m('div.mb3', [
    m('label.db.mb1', { for: inputId }, label),
    m('input.form-control.w-100', Object.assign({
      id: inputId,
      type: 'text',
      autocomplete: 'off',
      value: value(),
      oninput: function (event) { value(event.target.value); }
    }, attributes || {})),
    help ? m('div.f6.gray.mt1', help) : null
  ]);
}

var sharePointDelivery = {
  oninit: function () {
    return loadSettings();
  },
  view: function () {
    if (state.loading) {
      return m('div.mt4.tc', m('img.h1', { src: '/images/loading-bars.svg' }));
    }
    var settings = state.settings || {};
    return m('section.mt4.pt4.bt.b--black-10', [
      m('h3.mt0.mb2', 'SharePoint Delivery'),
      m('p.mt0.gray', 'Configure the Microsoft Entra application Nimrod uses to upload scheduled report files. Folder access for analysts is managed in SharePoint.'),
      state.error().length ? m(flash, { items: state.error(), bg: 'bg-red' }) : null,
      state.message().length ? m(flash, { items: state.message(), bg: 'bg-green' }) : null,
      m('div.flex.items-center.justify-between.pa3.mb2.ba.b--black-10.br2', [
        m('div.pr3', [
          m('div', m('strong', 'Enable SharePoint delivery')),
          m('div.f6.gray.mt1', 'Controls whether Nimrod may generate and upload scheduled reports to SharePoint.')
        ]),
        m(ToggleSwitch, {
          id: 'sharepoint-delivery-enabled-toggle',
          checked: Boolean(state.enabled()),
          ariaLabel: 'Enable SharePoint delivery',
          onchange: function (event) {
            state.enabled(Boolean(event.target.checked));
          }
        })
      ]),
      connectionStatus(),
      !settings.credentialEncryptionAvailable
        ? m('div.pa2.mt2.br2.bg-washed-red.dark-red', 'Credential encryption is unavailable. Configure a report credential encryption key before saving a credential.')
        : null,
      m('div.flex-ns.mt3', [
        m('div.w-50-ns.pr3-ns', [
          field('Tenant ID', state.tenantId, null, 'The Microsoft Entra directory tenant ID.'),
          field('Client ID', state.clientId, null, 'The application registration client ID.')
        ]),
        m('div.w-50-ns.pl3-ns', [
          field('Document Library ID', state.driveId, null, 'The Microsoft Graph drive ID for the SharePoint document library.'),
          field('Default Folder', state.defaultFolder, { placeholder: 'Scheduled Reports' }, 'Optional. New schedules start with this folder, and connection tests verify it.')
        ])
      ]),
      m('div.mb3', [
        m('label.db.mb1', { for: 'sharepoint-authentication' }, 'Authentication'),
        m('select.form-control.w-50-ns', {
          id: 'sharepoint-authentication',
          value: state.authMethod(),
          onchange: function (event) { state.authMethod(event.target.value); }
        }, [
          m('option', { value: 'certificate' }, 'Certificate (recommended)'),
          m('option', { value: 'clientSecret' }, 'Client secret')
        ])
      ]),
      state.authMethod() === 'certificate' ? certificateSummary() : m('div.mb3', [
        m('label.db.mb1', { for: 'sharepoint-client-secret' }, settings.hasClientSecret ? 'Replace Client Secret' : 'Client Secret'),
        m('input.form-control.w-50-ns', {
          id: 'sharepoint-client-secret',
          type: 'password',
          autocomplete: 'new-password',
          placeholder: settings.hasClientSecret ? 'Leave blank to keep the current secret' : '',
          value: state.clientSecret(),
          oninput: function (event) { state.clientSecret(event.target.value); }
        }),
        m('div.f6.gray.mt1', 'Client secrets remain available for migration and development. Certificates are recommended for production.')
      ]),
      m('div.flex.justify-end.items-center', [
        m(buttonElement, {
          label: state.testing ? 'Testing…' : 'Test Saved Connection',
          class: 'mr2',
          disabled: state.testing || !settings.configured,
          func: testConnection
        }),
        m(buttonElement, {
          label: state.saving ? 'Saving…' : 'Save Settings',
          class: 'green',
          style: { minWidth: '33%' },
          disabled: state.saving || !settings.credentialEncryptionAvailable,
          func: saveSettings
        })
      ])
    ]);
  }
};

module.exports = sharePointDelivery;
